/**
 * Inventory Hub — tabbed interface for all inventory features.
 * Uses URL-based tab routing for deep-linkable tabs.
 * Keep-alive rendering: ALL tabs stay mounted (hidden via display:none).
 */
import React, { useMemo } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import InventoryList from './InventoryList';
import StockStatus from './StockStatus';
import StockOutManagement from '../StockOut/StockOutManagement';
import BatchInventoryDashboard from '../BatchInventory/BatchInventoryDashboard';

const tabs = [
  { label: 'Inventory Level', path: '/inventory', Component: InventoryList },
  { label: 'Stockout Management', path: '/inventory/stockout-management', Component: StockOutManagement },
  { label: 'Stock Status', path: '/inventory/stock-status', Component: StockStatus },
  { label: 'Expiry & Batch Tracking', path: '/inventory/batch-tracking', Component: BatchInventoryDashboard },
];

const InventoryHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTabIndex = useMemo(() => {
    const idx = tabs.findIndex((t) => location.pathname === t.path);
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].path);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed header: title + tabs (do not scroll) */}
      <Box sx={{ flexShrink: 0 }}>
        {/* Page title */}
        <Box sx={{ mb: '20px' }}>
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Inventory
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
            Stock levels, stockout management, transfers, status, and batch tracking
          </Typography>
        </Box>

        {/* Tab navigation */}
        <Box sx={{ borderBottom: '1px solid var(--color-border)', mb: 3 }}>
          <Tabs
            value={activeTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab) => (
              <Tab key={tab.path} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Scrollable tab content area — only this scrolls */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* Keep-alive tab content */}
        {tabs.map((tab, idx) => {
          const TabComponent = tab.Component;
          return (
            <Box
              key={tab.path}
              sx={{ display: activeTabIndex === idx ? 'block' : 'none' }}
            >
              <TabComponent />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default InventoryHub;
